import React, { useEffect } from "react";
import { Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector, selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import { ENGAGEMENT_TYPES } from "@consts";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import { HyroButton, HyroTabs } from "@hyro-components";
import HyroBreadcrumbs from "@hyro-components/hyro-breadcrumbs/HyroBreadcrumbs";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";
import { TABS, PAGE_TITLE, ASSISTANT_CHANNEL_TO_BENCHMARK_CHANNEL } from "./AgentSettings.consts";
import GeneralSettingsTab from "./general/GeneralSettingsTab";
import DeploymentSettingsTab from "./deployment/DeploymentSettingsTab";
import AnalyticsSettingsTab from "./analytics/AnalyticsSettingsTab";
import PublishDialog from "src/pages/Settings/PublishDialog";

const BREADCRUMBS = ["All agents", "Agent settings"];
const BASE_URL = "/admin/all-agents";

const AgentSettingsContent = ({
  isAdminContext,
  assistantSettingsProps,
  areSettingsChanged,
  isLoading,
  isPublishDialogOpen,
  setIsPublishDialogOpen,
  onPublish,
  settingsInitialData,
  deploymentFieldProps,
  isDeploymentSubmitting,
  analyticsSettingsProps,
  isReadOnly
}) => {
  const hasPermission = useAppStore(hasPermissionSelector);
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  // Benchmarks exist for voice and chat agents only — SMS has no benchmark metrics defined,
  // and outbound agents have no benchmarked funnel.
  const hasBenchmarks =
    !!ASSISTANT_CHANNEL_TO_BENCHMARK_CHANNEL[selectedAssistant?.channel] &&
    selectedAssistant?.engagementType !== ENGAGEMENT_TYPES.OUTBOUND;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || TABS.GENERAL;

  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: TABS.GENERAL }, { replace: true });
    }
  }, []);

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  return (
    <>
      {isAdminContext && <HyroBreadcrumbs breadcrumbs={BREADCRUMBS} baseUrl={BASE_URL} />}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={isAdminContext ? 2 : 0} mb={3} sx={{ height: 36 }}>
        <Typography variant="h5" data-testid="agent-settings-title">
          {PAGE_TITLE}
        </Typography>
        <HyroButton
          data-testid="agent-settings-save-changes"
          loading={isLoading}
          onClick={() => setIsPublishDialogOpen(true)}
          disabled={!areSettingsChanged || isReadOnly}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.PRIMARY}
        >
          Save changes
        </HyroButton>
      </Stack>

      <HyroTabs
        selectedValue={activeTab}
        onTabChange={handleTabChange}
        tabsSpec={[
          {
            label: "General",
            value: TABS.GENERAL,
            content: (
              <GeneralSettingsTab
                assistantSettingsProps={assistantSettingsProps}
                settingsInitialData={settingsInitialData}
                isReadOnly={isReadOnly}
              />
            )
          },
          ...hasBenchmarks ? [{
            label: "Analytics",
            value: TABS.ANALYTICS,
            content: <AnalyticsSettingsTab analyticsSettingsProps={analyticsSettingsProps} />
          }] : [],
          ...hasPermission(USER_PERMISSIONS.ABILITIES.DEPLOYMENT_SETTINGS) ? [{
            label: "Deployment",
            value: TABS.DEPLOYMENT,
            content: (
              <DeploymentSettingsTab
                deploymentFieldProps={deploymentFieldProps}
                isDeploymentSubmitting={isDeploymentSubmitting}
              />
            )
          }] : [],
        ]}
      />

      <PublishDialog
        isOpen={isPublishDialogOpen}
        isLoading={isLoading}
        onCancel={() => setIsPublishDialogOpen(false)}
        onSumbit={onPublish}
        title="Publish Settings Changes"
      />
    </>
  );
};

export default AgentSettingsContent;
